import { useApi } from "@/lib/api"
import { WorkerCompensationResponse, WCBRateResponse, WorkerCompensationItem } from "@/lib/config"
import { useMemo } from "react"

/**
 * Hook-based WCB API service using the centralized useApi hook
 * Replaces the class-based WcbApiService with hook-based approach
 */
export function useWcbApi() {
  const { mainApi } = useApi()

  /**
   * Retrieves all available worker compensation codes from the Phoenix API
   */
  const getWorkerCompensations = async (): Promise<WorkerCompensationResponse> => {
    return mainApi.get<WorkerCompensationResponse>("/api/Payroll/getAllWorkerCompensations")
  }

  /**
   * Retrieves worker compensation codes for a specific subdivision
   * @param subdivisionId - The subdivision ID to filter worker compensations
   */
  const getWorkerCompensationsBySubdivision = async (subdivisionId: string): Promise<WorkerCompensationResponse> => {
    const path = `/api/Payroll/getAllWorkerCompensations?$filter=SubdivisionId eq ${subdivisionId}`
    return mainApi.get<WorkerCompensationResponse>(path)
  }

  /**
   * Retrieves WCB rates for a specific subdivision and organization combination
   */
  const getWCBRatesBySubdivisionAndOrganization = async (
    subdivisionId: number, 
    organizationId: number
  ): Promise<WCBRateResponse | null> => {
    try {
      const path = `/api/Payroll/getWCBCodesBySubdivisionId/${subdivisionId}/${organizationId}`
      const response = await mainApi.get<WCBRateResponse>(path)
      
      if (!response) {
        console.warn(`No WCB rate data returned for subdivision ${subdivisionId}, organization ${organizationId}`)
        return null
      }
      
      return response
    } catch (error) {
      console.warn(`Failed to get WCB rates for subdivision ${subdivisionId}, organization ${organizationId}:`, error)
      return null
    }
  }

  /**
   * Calculates the organization-specific WCB rate for a given worker compensation code
   */
  const getWCBRateForCode = async (
    wcbCode: string, 
    subdivisionId: number, 
    organizationId: number
  ): Promise<number> => {
    if (!wcbCode || !subdivisionId || !organizationId) {
      console.warn('Invalid parameters for WCB rate lookup:', { wcbCode, subdivisionId, organizationId })
      return 0
    }

    try {
      console.log(`Looking up WCB rate for code "${wcbCode}", subdivision ${subdivisionId}, organization ${organizationId}`)
      
      const ratesResponse = await getWCBRatesBySubdivisionAndOrganization(subdivisionId, organizationId)
      
      if (!ratesResponse) {
        console.warn(`No rate data available for subdivision ${subdivisionId}, organization ${organizationId}`)
        return 0
      }

      if (!ratesResponse.Items || !Array.isArray(ratesResponse.Items) || ratesResponse.Items.length === 0) {
        console.warn(`No WCB rate items found for subdivision ${subdivisionId}, organization ${organizationId}`)
        return 0
      }
      
      const wcbDetail = ratesResponse.Items.find(detail => {
        if (!detail || !detail.WorkerCompensation) {
          console.warn('Invalid WCB detail structure found:', detail)
          return false
        }
        return detail.WorkerCompensation.Code === wcbCode
      })

      if (!wcbDetail) {
        console.warn(`WCB code "${wcbCode}" not found in subdivision ${subdivisionId} for organization ${organizationId}`)
        return 0
      }

      if (!wcbDetail.WcbSubdivisionDetailOrganizationInternalRole || 
          !Array.isArray(wcbDetail.WcbSubdivisionDetailOrganizationInternalRole) ||
          wcbDetail.WcbSubdivisionDetailOrganizationInternalRole.length === 0) {
        console.warn(`No organization-specific rates found for WCB code "${wcbCode}", subdivision ${subdivisionId}`)
        return 0
      }

      const organizationRate = wcbDetail.WcbSubdivisionDetailOrganizationInternalRole.find(
        rate => {
          if (!rate) {
            console.warn('Invalid organization rate structure found:', rate)
            return false
          }
          return rate.OrganizationId === organizationId
        }
      )

      if (!organizationRate) {
        console.warn(`No rate found for organization ${organizationId} with WCB code "${wcbCode}" in subdivision ${subdivisionId}`)
        
        const availableOrgs = wcbDetail.WcbSubdivisionDetailOrganizationInternalRole
          .map(rate => rate?.OrganizationId)
          .filter(id => id !== undefined)
        console.log(`Available organization IDs for this WCB code: [${availableOrgs.join(', ')}]`)
        
        return 0
      }

      const ratePercent = organizationRate.RatePercent ?? 0
      console.log(`Found WCB rate for code "${wcbCode}": ${ratePercent}%`)
      
      return ratePercent
    } catch (error) {
      console.error(`Unexpected error getting WCB rate for code "${wcbCode}", subdivision ${subdivisionId}, organization ${organizationId}:`, error)
      return 0
    }
  }

  /**
   * Retrieves worker compensation details by code
   */
  const getWorkerCompensationByCode = async (code: string): Promise<WorkerCompensationItem | null> => {
    try {
      const path = `/api/Payroll/getAllWorkerCompensations?$filter=Code eq '${code}'`
      const response = await mainApi.get<WorkerCompensationResponse>(path)
      return response.Items?.[0] || null
    } catch (error) {
      console.error(`Failed to get worker compensation for code ${code}:`, error)
      return null
    }
  }

  return useMemo(() => ({
    getWorkerCompensations,
    getWorkerCompensationsBySubdivision,
    getWCBRatesBySubdivisionAndOrganization,
    getWCBRateForCode,
    getWorkerCompensationByCode,
  }), []);
}
